<script lang="ts">
  import type { PlayerProfile } from '@2k48/shared';
  import TopBar from '../components/TopBar.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import PixelPanel from '../components/PixelPanel.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import SegmentedBar from '../components/SegmentedBar.svelte';
  import WaveText from '../components/WaveText.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { session } from '../stores/session.svelte';
  import { router } from '../stores/router.svelte';
  import { api } from '../../net/client';
  import { groupNumber } from '../format';

  /**
   * Everything on this screen is server-computed. The client asks who it is and
   * renders the answer; it never adds up its own matches, because a number the
   * client can compute is a number the client can inflate.
   */

  interface ProfileStats {
    soloBest: number | null;
    versusRating: number | null;
    soloPlayed: number;
    versusPlayed: number;
    matchesPlayed: number;
    versusWins: number;
    bestScore: number;
    maxTile: number;
    maxCombo: number;
    bestWinStreak: number;
    achievementsUnlocked: number;
    achievementsTotal: number;
  }

  interface ProfileResponse {
    profile: PlayerProfile;
    stats: ProfileStats;
  }

  const t = $derived(i18n.t);

  const playerId = $derived(router.current.params.player || session.profile?.id);
  const isOwnProfile = $derived(playerId === session.profile?.id);
  let viewedProfile = $state<PlayerProfile | null>(null);
  let stats = $state<ProfileStats | null>(null);
  let loading = $state(true);

  $effect(() => {
    const player = playerId;
    viewedProfile = null;
    stats = null;
    if (!player) {
      loading = false;
      return;
    }

    let cancelled = false;
    loading = true;

    void api<ProfileResponse>(`/profile/${player}`)
      .then((response) => {
        if (cancelled) return;
        viewedProfile = response.profile;
        stats = response.stats;
      })
      .catch(() => {
        if (cancelled) return;
        viewedProfile = null;
        stats = null;
      })
      .finally(() => {
        if (!cancelled) loading = false;
      });

    return () => {
      cancelled = true;
    };
  });

  const identity = $derived(viewedProfile ?? (isOwnProfile ? session.profile : null));
  const losses = $derived(stats ? Math.max(0, stats.versusPlayed - stats.versusWins) : 0);

  function openPlayerView(view: 'achievements' | 'history' | 'highscores'): void {
    router.go(view, playerId ? { player: playerId } : {});
  }
</script>

<div class="view profile">
  <TopBar title={t.profile.title} />

  <div class="view__body">
    {#if identity}
      <div class="profile__identity">
        <PixelAvatar avatarId={identity.avatarId} />
        <span class="profile__name">{identity.displayName}</span>
      </div>
    {/if}

    {#if loading}
      <p class="view__placeholder">{t.common.loading}</p>
    {:else if !stats}
      <p class="view__placeholder">
        {isOwnProfile ? t.profile.offlineMessage : t.profile.offlineMessagePublic}
      </p>
    {:else}
      <PixelPanel variant="accent" class="profile__hero">
        <span class="lbl">{isOwnProfile ? t.profile.bestScore : t.profile.bestScorePublic}</span>
        <WaveText class="profile__hero-value" text={groupNumber(stats.bestScore)} />
        <span class="profile__hero-tags">
          <span class="badge badge--cyan">{stats.maxTile || '-'}</span>
          <span class="badge">{t.common.combo(stats.maxCombo)}</span>
        </span>
      </PixelPanel>

      <div class="profile__stats">
        <span class="stat">
          <span class="stat__label">{t.profile.versusElo}</span>
          <span class="stat__value">{stats.versusRating ?? '-'}</span>
        </span>
        <span class="stat">
          <span class="stat__label">{t.profile.matches}</span>
          <span class="stat__value">{groupNumber(stats.matchesPlayed)}</span>
        </span>
        <span class="stat">
          <span class="stat__label">{t.profile.winsLosses}</span>
          <span class="stat__value">{stats.versusWins} / {losses}</span>
        </span>
        <span class="stat">
          <span class="stat__label">{t.profile.bestStreak}</span>
          <span class="stat__value">{stats.bestWinStreak}</span>
        </span>
      </div>

      <PixelPanel class="profile__achievements">
        <span class="profile__achievements-head">
          <span class="lbl">{t.profile.achievements}</span>
          <span class="profile__achievements-count">
            {stats.achievementsUnlocked}<span class="achievements__total"
              >/{stats.achievementsTotal}</span
            >
          </span>
        </span>
        <SegmentedBar value={stats.achievementsUnlocked} max={stats.achievementsTotal} />
      </PixelPanel>

      <div class="profile__actions">
        <PixelButton variant="ghost" icon="trophy" onclick={() => openPlayerView('achievements')}>
          {t.profile.viewAchievements}
        </PixelButton>
        <PixelButton variant="ghost" icon="timer" onclick={() => openPlayerView('history')}>
          {t.profile.history}
        </PixelButton>
        <PixelButton variant="ghost" icon="crown" onclick={() => openPlayerView('highscores')}>
          {t.profile.highScores}
        </PixelButton>
      </div>
    {/if}
  </div>
</div>
